import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import urlParse from 'url-parse';
import qs from 'querystringify';
import {
  Button,
  ButtonGroup,
  Card,
  Content,
  Flex,
  Page,
} from '../../components';
import api from '../../utils/services';
import { getIFrameId, getTheme, getUserDetails } from '../../modules/selectors';
import sendHeightPostMessage from '../../utils/sendHeightPostMessage';
import { WindowEventListener } from '../../listeners';


const propTypes = {
  iFrameId: PropTypes.string,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      carrierId: PropTypes.string,
      contentId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  // Listen for a theme change since it may change the components height
  theme: PropTypes.shape({}),
  user: PropTypes.shape({}),
};

const defaultProps = {
  iFrameId: null,
  theme: null,
  user: null,
};

const ContentHolder = styled.div`
  &:hover {
    background-color: ${props => (props.editAvailable ? 'rgba(0, 0, 0, 0.1)' : 'inherit')};
  }
`;

class ViewContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      content: null,
      editMode: false,
      loading: true,
      originalContent: null,
      saving: false,
    };
  }

  componentDidMount() {
    const { match: { params: { contentId } } } = this.props;
    api.getContents(contentId).then((content) => {
      this.setState({ content });
    }).finally(() => {
      this.setState({ loading: false });
    });

    this.sendHeightUpdate();
  }

  componentDidUpdate() {
    this.sendHeightUpdate();
  }

  sendHeightUpdate = () => {
    const { iFrameId } = this.props;
    sendHeightPostMessage(iFrameId);
  }

  handleCancelEdit = () => {
    this.setState(state => ({
      content: JSON.parse(JSON.stringify(state.originalContent)),
      editMode: false,
    }));
  }

  handleContentValueChange = (newValue) => {
    this.setState(state => ({
      content: {
        ...state.content,
        value: newValue,
      },
    }));
  }

  handleSaveEdit = () => {
    const { content } = this.state;

    this.setState({ saving: true });
    api.updateContent(content.id, content)
      .then(() => {
        this.setState({
          editMode: false,
          saving: false,
        });
      });
  }

  handleStartEdit = () => {
    const { user } = this.props;
    if (user) {
      this.setState(state => ({
        editMode: true,
        originalContent: JSON.parse(JSON.stringify(state.content)),
      }));
    }
  }

  render() {
    const {
      location: { search },
      match: { params: { carrierId, contentId } },
      user,
    } = this.props;
    const {
      content,
      editMode,
      loading,
      saving,
    } = this.state;

    const editAllowed = !!user;

    const contentMarkup = (
      <ContentHolder
        editAvailable={editAllowed && !editMode}
        onClick={editAllowed && !editMode ? this.handleStartEdit : undefined}
      >
        {content && (
          <Content
            edit={editMode}
            onChange={this.handleContentValueChange}
            carrierId={content.carrierId}
            type={content.type}
            value={content.value}
          />
        )}
      </ContentHolder>
    );

    let editModeButtons = null;
    let startEditButton = null;
    if (editMode) {
      editModeButtons = (
        <ButtonGroup>
          <Button onClick={this.handleCancelEdit}>Cancel</Button>
          <Button primary loading={saving} onClick={this.handleSaveEdit}>Save</Button>
        </ButtonGroup>
      );
    } else if (editAllowed) {
      startEditButton = <Button primary onClick={this.handleStartEdit}>Edit</Button>;
    }

    const { query } = urlParse(search);
    const { frameless } = qs.parse(query);
    if (frameless !== undefined) {
      return (
        <React.Fragment>
          {contentMarkup}
          <Flex paddingTop="1em">
            <Flex.Item marginStart="auto">
              {editModeButtons}
            </Flex.Item>
          </Flex>
          <WindowEventListener event="resize" onEvent={this.sendHeightUpdate} />
        </React.Fragment>
      );
    }

    let breadcrumbHref = '/contents';
    let editHref = `/contents/${contentId}/edit`;
    if (carrierId) {
      breadcrumbHref = `/carriers/${carrierId}/contents`;
      editHref = `/carriers/${carrierId}/contents/${contentId}/edit`;
    }

    return (
      <Page
        breadcrumbs={user ? [{ content: 'Contents', href: breadcrumbHref }] : undefined}
        title={(content && content.name) || (loading && 'Loading...') || ''}
        primaryAction={user ? { content: 'Advanced Edit', href: editHref } : undefined}
      >
        <Card loading={loading}>
          <Card.Block>
            {contentMarkup}
            <Flex paddingTop="1em">
              <Flex.Item marginStart="auto">
                {editModeButtons || startEditButton}
              </Flex.Item>
            </Flex>
          </Card.Block>
        </Card>
      </Page>
    );
  }
}

function mapStateToProps(state) {
  return {
    iFrameId: getIFrameId(state),
    theme: getTheme(state),
    user: getUserDetails(state),
  };
}
ViewContent.propTypes = propTypes;
ViewContent.defaultProps = defaultProps;
export default connect(mapStateToProps)(ViewContent);
